import { useState, useEffect, useCallback } from 'react'
import { customerApi } from '../services/api'
import ColorPickerField from '../components/widget-settings/ColorPickerField'
import SuggestedQuestionsEditor from '../components/widget-settings/SuggestedQuestionsEditor'
import WidgetPreview from '../components/widget-settings/WidgetPreview'
import ConfigSection from '../components/widget-settings/ConfigSection'
import PresetSelector from '../components/widget-settings/PresetSelector'
import EmbedCodeDisplay from '../components/widget-settings/EmbedCodeDisplay'
import {
  PaintBrushIcon,
  TagIcon,
  CursorArrowRaysIcon,
  ChatBubbleLeftEllipsisIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import {
  CheckCircleIcon as CheckCircleSolid,
  ExclamationCircleIcon as ExclamationSolid,
} from '@heroicons/react/24/solid'

const DEFAULT_CONFIG = {
  appearance: {
    primaryColor: '#6366f1',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    headerColor: '#6366f1',
    userMessageColor: '#6366f1',
    assistantMessageColor: '#f3f4f6',
    fontFamily: 'system-ui',
    borderRadius: 12,
  },
  branding: {
    chatbotName: 'Assistant',
    avatarUrl: null,
    headerTitle: 'Chat with us',
    inputPlaceholder: 'Type a message...',
  },
  behavior: {
    position: 'bottom-right',
    offsetX: 20,
    offsetY: 20,
    autoOpenDelay: 0,
    showOnMobile: true,
    mobileBreakpoint: 768,
  },
  messages: {
    welcomeMessage: 'Hi! How can I help you today?',
    offlineMessage: "We're currently offline. Leave us a message!",
    errorMessage: 'Sorry, something went wrong. Please try again.',
    suggestedQuestions: [],
  },
}

const SECTIONS = [
  { id: 'appearance', label: 'Appearance', icon: PaintBrushIcon, description: 'Colors, fonts, and visual style' },
  { id: 'branding', label: 'Branding', icon: TagIcon, description: 'Name, avatar, and titles' },
  { id: 'behavior', label: 'Behavior', icon: CursorArrowRaysIcon, description: 'Position, timing, and responsiveness' },
  { id: 'messages', label: 'Messages', icon: ChatBubbleLeftEllipsisIcon, description: 'Welcome, error, and suggested messages' },
]

const FONT_OPTIONS = [
  { value: 'system-ui', label: 'System Default' },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: "'Roboto', sans-serif", label: 'Roboto' },
  { value: "'Open Sans', sans-serif", label: 'Open Sans' },
  { value: "'Lato', sans-serif", label: 'Lato' },
  { value: "'Poppins', sans-serif", label: 'Poppins' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'monospace', label: 'Monospace' },
]

export default function WidgetSettings({ apiKey }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [originalConfig, setOriginalConfig] = useState(DEFAULT_CONFIG)
  const [embedCode, setEmbedCode] = useState('')
  const [activeSection, setActiveSection] = useState('appearance')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [toast, setToast] = useState(null) // { type: 'success'|'error', message: '' }

  // Detect unsaved changes
  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig)

  // ============================================================
  // Data Fetching
  // ============================================================

  useEffect(() => {
    fetchConfig()
  }, [])

  // beforeunload warning for unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (hasChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasChanges])

  const fetchConfig = async () => {
    setIsLoading(true)
    try {
      const response = await customerApi.get('/customer/widget-config')
      const data = response.data
      const fetched = {
        appearance: { ...DEFAULT_CONFIG.appearance, ...data.config?.appearance },
        branding: { ...DEFAULT_CONFIG.branding, ...data.config?.branding },
        behavior: { ...DEFAULT_CONFIG.behavior, ...data.config?.behavior },
        messages: { ...DEFAULT_CONFIG.messages, ...data.config?.messages },
      }
      setConfig(fetched)
      setOriginalConfig(fetched)
      if (data.embed_code) {
        setEmbedCode(data.embed_code)
      }
    } catch (err) {
      console.error('Failed to fetch widget config:', err)
      showToast('error', err.response?.data?.detail || 'Failed to load widget configuration.')
    } finally {
      setIsLoading(false)
    }
  }

  // ============================================================
  // Actions
  // ============================================================

  const showToast = useCallback((type, message) => {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }, [])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const response = await customerApi.put('/customer/widget-config', {
        appearance: config.appearance,
        branding: config.branding,
        behavior: config.behavior,
        messages: config.messages,
      })
      if (response.data.success) {
        const saved = {
          appearance: { ...DEFAULT_CONFIG.appearance, ...response.data.config?.appearance },
          branding: { ...DEFAULT_CONFIG.branding, ...response.data.config?.branding },
          behavior: { ...DEFAULT_CONFIG.behavior, ...response.data.config?.behavior },
          messages: { ...DEFAULT_CONFIG.messages, ...response.data.config?.messages },
        }
        setConfig(saved)
        setOriginalConfig(saved)
        showToast('success', 'Widget settings saved successfully.')
      } else {
        showToast('error', 'Failed to save widget settings.')
      }
    } catch (err) {
      console.error('Failed to save widget config:', err)
      showToast('error', err.response?.data?.detail || 'Failed to save widget settings.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = async () => {
    if (!confirm('Reset widget settings to defaults? This cannot be undone.')) return

    try {
      const response = await customerApi.post('/customer/widget-config/reset')
      if (response.data.success) {
        const reset = {
          appearance: { ...DEFAULT_CONFIG.appearance, ...response.data.config?.appearance },
          branding: { ...DEFAULT_CONFIG.branding, ...response.data.config?.branding },
          behavior: { ...DEFAULT_CONFIG.behavior, ...response.data.config?.behavior },
          messages: { ...DEFAULT_CONFIG.messages, ...response.data.config?.messages },
        }
        setConfig(reset)
        setOriginalConfig(reset)
        showToast('success', 'Widget settings have been reset to defaults.')
      }
    } catch (err) {
      console.error('Failed to reset widget config:', err)
      showToast('error', err.response?.data?.detail || 'Failed to reset widget settings.')
    }
  }

  // ============================================================
  // Config update helpers
  // ============================================================

  const updateAppearance = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      appearance: { ...prev.appearance, [key]: value },
    }))
  }

  const updateBranding = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      branding: { ...prev.branding, [key]: value },
    }))
  }

  const updateBehavior = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      behavior: { ...prev.behavior, [key]: value },
    }))
  }

  const updateMessages = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      messages: { ...prev.messages, [key]: value },
    }))
  }

  const applyPreset = (appearanceValues) => {
    setConfig((prev) => ({
      ...prev,
      appearance: { ...prev.appearance, ...appearanceValues },
    }))
  }

  // ============================================================
  // Loading State
  // ============================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin mx-auto" />
          <p className="text-sm text-slate-500">Loading widget settings...</p>
        </div>
      </div>
    )
  }

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="space-y-6">
      {/* Toast notifications */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 max-w-sm animate-slide-in">
          <div
            className={`flex items-start space-x-3 bg-white border rounded-xl p-4 shadow-lg ${
              toast.type === 'success' ? 'border-emerald-200' : 'border-red-200'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircleSolid className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            ) : (
              <ExclamationSolid className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900">
                {toast.type === 'success' ? 'Success' : 'Error'}
              </p>
              <p className="text-sm text-slate-500 mt-0.5">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast(null)}
              className="text-slate-400 hover:text-slate-600"
            >
              <span className="sr-only">Close</span>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Widget Customization
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Customize the look, feel, and behavior of your chat widget.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              className="flex items-center space-x-1.5 px-4 py-2 border border-slate-200 text-sm font-medium text-slate-600 rounded-xl hover:bg-slate-50 transition-colors"
            >
              <ArrowPathIcon className="h-4 w-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="relative flex items-center space-x-1.5 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving && (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              )}
              <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
              {hasChanges && !isSaving && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border-2 border-white" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main content: two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Section tabs + config fields */}
        <div className="space-y-4">
          {/* Section tab buttons */}
          <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl overflow-x-auto">
            {SECTIONS.map((section) => {
              const Icon = section.icon
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    activeSection === section.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{section.label}</span>
                </button>
              )
            })}
          </div>

          {/* Appearance section */}
          <ConfigSection
            title="Colors & Style"
            description="Set the color scheme and visual properties"
            isActive={activeSection === 'appearance'}
            onClick={() =>
              setActiveSection(activeSection === 'appearance' ? '' : 'appearance')
            }
          >
            <PresetSelector onSelect={applyPreset} />

            <ColorPickerField
              label="Primary Color"
              value={config.appearance.primaryColor}
              onChange={(v) => updateAppearance('primaryColor', v)}
            />
            <ColorPickerField
              label="Background Color"
              value={config.appearance.backgroundColor}
              onChange={(v) => updateAppearance('backgroundColor', v)}
            />
            <ColorPickerField
              label="Text Color"
              value={config.appearance.textColor}
              onChange={(v) => updateAppearance('textColor', v)}
            />
            <ColorPickerField
              label="Header Color"
              value={config.appearance.headerColor}
              onChange={(v) => updateAppearance('headerColor', v)}
            />
            <ColorPickerField
              label="User Message Color"
              value={config.appearance.userMessageColor}
              onChange={(v) => updateAppearance('userMessageColor', v)}
            />
            <ColorPickerField
              label="Assistant Message Color"
              value={config.appearance.assistantMessageColor}
              onChange={(v) => updateAppearance('assistantMessageColor', v)}
            />

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Font Family
              </label>
              <select
                value={config.appearance.fontFamily}
                onChange={(e) => updateAppearance('fontFamily', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                {FONT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Border Radius ({config.appearance.borderRadius}px)
              </label>
              <input
                type="range"
                min={0}
                max={24}
                value={config.appearance.borderRadius}
                onChange={(e) =>
                  updateAppearance('borderRadius', parseInt(e.target.value))
                }
                className="w-full accent-indigo-600"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>Square</span>
                <span>Rounded</span>
              </div>
            </div>
          </ConfigSection>

          {/* Branding section */}
          <ConfigSection
            title="Branding"
            description="Chatbot name, avatar, and display text"
            isActive={activeSection === 'branding'}
            onClick={() =>
              setActiveSection(activeSection === 'branding' ? '' : 'branding')
            }
          >
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Chatbot Name
              </label>
              <input
                type="text"
                value={config.branding.chatbotName}
                onChange={(e) => updateBranding('chatbotName', e.target.value)}
                placeholder="Assistant"
                maxLength={50}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Header Title
              </label>
              <input
                type="text"
                value={config.branding.headerTitle}
                onChange={(e) => updateBranding('headerTitle', e.target.value)}
                placeholder="Chat with us"
                maxLength={60}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Avatar URL (optional)
              </label>
              <input
                type="text"
                value={config.branding.avatarUrl || ''}
                onChange={(e) =>
                  updateBranding('avatarUrl', e.target.value || null)
                }
                placeholder="https://example.com/avatar.png"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-slate-400 mt-1">
                Leave empty to use the chatbot name initial as avatar.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Input Placeholder
              </label>
              <input
                type="text"
                value={config.branding.inputPlaceholder}
                onChange={(e) =>
                  updateBranding('inputPlaceholder', e.target.value)
                }
                placeholder="Type a message..."
                maxLength={80}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
            </div>
          </ConfigSection>

          {/* Behavior section */}
          <ConfigSection
            title="Behavior"
            description="Widget position, timing, and mobile settings"
            isActive={activeSection === 'behavior'}
            onClick={() =>
              setActiveSection(activeSection === 'behavior' ? '' : 'behavior')
            }
          >
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Position
              </label>
              <select
                value={config.behavior.position}
                onChange={(e) => updateBehavior('position', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              >
                <option value="bottom-right">Bottom Right</option>
                <option value="bottom-left">Bottom Left</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Offset X (px)
                </label>
                <input
                  type="number"
                  value={config.behavior.offsetX}
                  onChange={(e) =>
                    updateBehavior('offsetX', parseInt(e.target.value) || 0)
                  }
                  min={0}
                  max={100}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Offset Y (px)
                </label>
                <input
                  type="number"
                  value={config.behavior.offsetY}
                  onChange={(e) =>
                    updateBehavior('offsetY', parseInt(e.target.value) || 0)
                  }
                  min={0}
                  max={100}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Auto-Open Delay (seconds)
              </label>
              <input
                type="number"
                value={config.behavior.autoOpenDelay}
                onChange={(e) =>
                  updateBehavior(
                    'autoOpenDelay',
                    Math.max(0, parseInt(e.target.value) || 0)
                  )
                }
                min={0}
                max={120}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-slate-400 mt-1">
                Set to 0 to disable auto-open. Widget opens after this many seconds.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Show on Mobile
                </label>
                <p className="text-xs text-slate-400">
                  Display the widget on mobile devices
                </p>
              </div>
              <button
                onClick={() =>
                  updateBehavior('showOnMobile', !config.behavior.showOnMobile)
                }
                className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                  config.behavior.showOnMobile ? 'bg-indigo-600' : 'bg-slate-200'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    config.behavior.showOnMobile
                      ? 'translate-x-5'
                      : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {config.behavior.showOnMobile && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Mobile Breakpoint (px)
                </label>
                <input
                  type="number"
                  value={config.behavior.mobileBreakpoint}
                  onChange={(e) =>
                    updateBehavior(
                      'mobileBreakpoint',
                      parseInt(e.target.value) || 768
                    )
                  }
                  min={320}
                  max={1024}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
            )}
          </ConfigSection>

          {/* Messages section */}
          <ConfigSection
            title="Messages"
            description="Welcome, offline, error, and suggested messages"
            isActive={activeSection === 'messages'}
            onClick={() =>
              setActiveSection(activeSection === 'messages' ? '' : 'messages')
            }
          >
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Welcome Message
              </label>
              <textarea
                value={config.messages.welcomeMessage}
                onChange={(e) =>
                  updateMessages('welcomeMessage', e.target.value)
                }
                placeholder="Hi! How can I help you today?"
                rows={2}
                maxLength={300}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Offline Message
              </label>
              <textarea
                value={config.messages.offlineMessage}
                onChange={(e) =>
                  updateMessages('offlineMessage', e.target.value)
                }
                placeholder="We're currently offline. Leave us a message!"
                rows={2}
                maxLength={300}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Error Message
              </label>
              <textarea
                value={config.messages.errorMessage}
                onChange={(e) => updateMessages('errorMessage', e.target.value)}
                placeholder="Sorry, something went wrong. Please try again."
                rows={2}
                maxLength={300}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all"
              />
            </div>

            <SuggestedQuestionsEditor
              questions={config.messages.suggestedQuestions}
              onChange={(q) => updateMessages('suggestedQuestions', q)}
            />
          </ConfigSection>
        </div>

        {/* Right: Live preview */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <WidgetPreview config={config} />
        </div>
      </div>

      {/* Embed code section */}
      <EmbedCodeDisplay apiKey={apiKey} embedCode={embedCode} />
    </div>
  )
}
